import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  dynamicApiErrorMessage,
  DynamicFrameworkApiService,
} from '../leads/smart-workflow/dynamic-framework-api.service';
import { DynamicWorkflowSummary } from '../leads/smart-workflow/dynamic-framework.models';
import { AuthService } from '../../services/auth.service';
import { workspaceNavigationInfo } from '../../services/workspace-route';
import { FeatureGroup, FeatureItem, visibleGroups } from './feature-config';

/**
 * Dashboard: a single-level, permission-filtered index of every app section.
 * Routed at /dashboard and rendered inside the workspace card shell.
 *
 * Business groups and Smart Workflow render as separate panels with their sections visible
 * immediately. Configuration is still derived from the same route metadata and
 * permissions, but lives in the page's Settings menu instead of a third panel.
 */
@Component({
  selector: 'app-dashboard-view',
  templateUrl: './dashboard-view.component.html',
  styleUrls: ['./dashboard-view.component.css'],
})
export class DashboardViewComponent implements OnInit, AfterViewInit {
  readonly smartWorkflowGroup = 'Smart Workflow';
  private static readonly WorkflowPageSize = 200;

  groups: FeatureGroup[] = [];
  configuration: FeatureGroup | null = null;
  publishedWorkflows: DynamicWorkflowSummary[] = [];
  workflowsLoading = false;
  workflowsError = '';

  private workflowLoadVersion = 0;

  @ViewChildren('groupPanel', { read: ElementRef })
  private groupPanels!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private workflows: DynamicFrameworkApiService,
  ) {}

  ngOnInit(): void {
    const visible = visibleGroups(this.router.config, p => this.auth.hasPermission(p));
    this.configuration = visible.find(g => g.title === 'Configuration') ?? null;
    this.groups = visible.filter(g => g.title !== 'Configuration');
    this.loadPublishedWorkflows();
  }

  ngAfterViewInit(): void {
    const requested = this.route.snapshot.queryParamMap.get('group');
    if (!requested) return;

    this.groupPanels.toArray()
      .find(panel => panel.nativeElement.dataset['group'] === requested)
      ?.nativeElement.scrollIntoView({ block: 'start' });
  }

  showSmartWorkflows(group: FeatureGroup): boolean {
    return group.title === this.smartWorkflowGroup;
  }

  activate(item: FeatureItem): void {
    this.router.navigateByUrl(item.route, {
      info: workspaceNavigationInfo('windowed'),
    });
  }

  openWorkflow(workflow: DynamicWorkflowSummary): void {
    this.router.navigate(['/smart-workflows', workflow.key], {
      info: workspaceNavigationInfo('windowed'),
    });
  }

  retryPublishedWorkflows(): void {
    this.loadPublishedWorkflows();
  }

  private loadPublishedWorkflows(): void {
    const version = ++this.workflowLoadVersion;
    this.publishedWorkflows = [];
    this.workflowsError = '';
    this.workflowsLoading = this.groups.some(group => this.showSmartWorkflows(group));
    if (!this.workflowsLoading) return;

    this.loadPublishedWorkflowPage(version, 1, []);
  }

  private loadPublishedWorkflowPage(
    version: number,
    page: number,
    items: DynamicWorkflowSummary[],
  ): void {
    this.workflows.listWorkflows({
      status: 'published',
      sort: 'label',
      page,
      pageSize: DashboardViewComponent.WorkflowPageSize,
    }).subscribe({
      next: result => {
        if (version !== this.workflowLoadVersion) return;
        const loaded = items.concat(result.items);
        if (loaded.length < result.total && result.items.length) {
          this.loadPublishedWorkflowPage(version, page + 1, loaded);
          return;
        }
        this.publishedWorkflows = loaded;
        this.workflowsLoading = false;
      },
      error: error => {
        if (version !== this.workflowLoadVersion) return;
        this.workflowsLoading = false;
        this.workflowsError = dynamicApiErrorMessage(
          error,
          'Could not load published workflows.',
        );
      },
    });
  }
}
